import React, { useState } from 'react';
import { FaClock, FaUser, FaTags, FaArrowRight } from 'react-icons/fa';
import './Blogs.css';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  tags: string[];
  featured?: boolean;
  link: string;
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: "Can the Okanagan's Fruit Industry Survive the Climate Crisis?",
    excerpt: "Are we witnessing the slow decline of the Okanagan's iconic fruit industry, or is there hope for adaptation and resilience?",
    content: "Full content here...",
    author: "Uchenna Anyaoha",
    date: "March 15, 2024",
    readTime: "5 min read",
    category: "Sustainability",
    image: "https://picsum.photos/seed/ml-systems/800/400",
    tags: ["Climate", "Agriculture", "Sustainability"],
    featured: true,
    link: "https://sinewslink.com/okanagan-fruit-industry-challenges-climate-change-solutions/"
  },
  {
    id: 2,
    title: "Turning the Tide: Empower fishers",
    excerpt: "What happens when the backbone of a community—its fishing industry—starts to buckle under environmental pressures?",
    content: "Full content here...",
    author: "Uchenna Anyaoha",
    date: "March 10, 2024",
    readTime: "4 min read",
    category: "Community",
    image: "https://picsum.photos/seed/fishing/800/400",
    tags: ["Fishing", "Community", "Environment"],
    featured: true,
    link: "https://sinewslink.com/atlantic-canada-fisheries-survival-climate-change-innovation/"
  },
  {
    id: 3,
    title: "SinewsLink Simplifies Local Commerce",
    excerpt: "SinewsLink helps local businesses and communities thrive with easy-to-use tools and, enhancing connections.",
    content: "Full content here...",
    author: "Uchenna Anyaoha",
    date: "March 5, 2024",
    readTime: "4 min read",
    category: "Technology",
    image: "https://picsum.photos/seed/local-commerce/800/400",
    tags: ["Commerce", "Technology", "Community"],
    link: "https://sinewslink.com/sinewslink-simplifies-local-commerce/"
  },
  {
    id: 4,
    title: "The Future of AI in Healthcare",
    excerpt: "Exploring how artificial intelligence is revolutionizing healthcare delivery and patient care.",
    content: "Full content here...",
    author: "Uchenna Anyaoha",
    date: "March 1, 2024",
    readTime: "5 min read",
    category: "Technology",
    image: "https://picsum.photos/seed/ai-healthcare/800/400",
    tags: ["AI", "Healthcare", "Innovation"],
    link: "#"
  },
  {
    id: 5,
    title: "Sustainable Technology Practices",
    excerpt: "How we can make technology more environmentally friendly and sustainable.",
    content: "Full content here...",
    author: "Uchenna Anyaoha",
    date: "February 28, 2024",
    readTime: "4 min read",
    category: "Sustainability",
    image: "https://picsum.photos/seed/sustainable-tech/800/400",
    tags: ["Sustainability", "Technology", "Environment"],
    link: "#"
  },
  {
    id: 6,
    title: "Building Resilient ML Systems",
    excerpt: "Best practices for creating robust and reliable machine learning systems.",
    content: "Full content here...",
    author: "Uchenna Anyaoha",
    date: "February 25, 2024",
    readTime: "6 min read",
    category: "Machine Learning",
    image: "https://picsum.photos/seed/okanagan-fruit/800/400",
    tags: ["ML", "Engineering", "Best Practices"],
    link: "#"
  }
];

const categories = [
  "All",
  "Technology",
  "Machine Learning",
  "Sustainability",
  "Engineering",
  "Innovation"
];

const Blogs: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [hoveredPost, setHoveredPost] = useState<number | null>(null);

  const filteredPosts = selectedCategory === "All"
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  const handleReadMore = (link: string) => {
    window.open(link, '_blank');
  };

  return (
    <div className="blogs-container">
      {/* Hero Section with Featured Posts */}
      <div className="featured-posts">
        {featuredPosts.map((post, index) => (
          <div
            key={post.id}
            className={`featured-post ${index === 0 ? 'main-feature' : ''}`}
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <div className="featured-image" style={{ backgroundImage: `url(${post.image})` }}>
              <div className="featured-overlay">
                <div className="featured-content">
                  <span className="featured-category">{post.category}</span>
                  <h2>{post.title}</h2>
                  <p>{post.excerpt}</p>
                  <div className="featured-meta">
                    <span><FaUser /> {post.author}</span>
                    <span><FaClock /> {post.readTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Categories */}
      <div className="categories-section">
        <h2>Browse by Category</h2>
        <div className="categories-list">
          {categories.map(category => (
            <button
              key={category}
              className={`category-button ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="blog-posts-grid">
        {filteredPosts.map(post => (
          <div
            key={post.id}
            className="blog-post-card"
            onMouseEnter={() => setHoveredPost(post.id)}
            onMouseLeave={() => setHoveredPost(null)}
          >
            <div className="post-image" style={{ backgroundImage: `url(${post.image})` }}>
              <div className={`post-overlay ${hoveredPost === post.id ? 'hovered' : ''}`}>
                <div className="post-content">
                  <span className="post-category">{post.category}</span>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="post-meta">
                    <span><FaUser /> {post.author}</span>
                    <span><FaClock /> {post.readTime}</span>
                  </div>
                  <div className="post-tags">
                    {post.tags.map(tag => (
                      <span key={tag} className="tag"><FaTags /> {tag}</span>
                    ))}
                  </div>
                  <button 
                    className="read-more"
                    onClick={() => handleReadMore(post.link)}
                  >
                    Read More <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Blogs;
